/**
 * Formate un montant en FCFA (entier, séparateur milliers = espace).
 * Ex: 1250000 → "1 250 000 FCFA"
 * Ex: -50000 → "-50 000 FCFA"
 * Ex: 1250000.50 → "1 250 001 FCFA" (arrondi)
 */
const FR_GROUP_SEP = /[\u202f\u00a0]/g;

function normalizeFrSpaces(s: string): string {
  return s.replace(FR_GROUP_SEP, " ");
}

export function formatMontantFCFA(montant: number | null | undefined, withSuffix = true): string {
  if (montant == null) return withSuffix ? "0 FCFA" : "0";

  const entier = Math.round(montant);

  const formatted = normalizeFrSpaces(
    new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(entier)
  );

  return withSuffix ? `${formatted} FCFA` : formatted;
}

/**
 * Formate un montant court (pour les tableaux compacts).
 * Ex: 1250000 → "1 250 000"
 */
export function formatMontant(montant: number | null | undefined): string {
  return formatMontantFCFA(montant, false);
}

/**
 * Parse un montant saisi (enlève espaces, virgule décimale → arrondi entier).
 * Ex: "1 250 000" → 1250000
 * Ex: "1250000,50" → 1250001 (arrondi)
 */
export function parseMontant(value: string): number {
  const cleaned = value
    .replace(/\s/g, "")
    .replace(/\u00a0/g, "")
    .replace(/\u202f/g, "")
    .replace(/,/g, ".")
    .replace(/[^\d.-]/g, "");

  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? Math.round(num) : 0;
}

/** Arrondit un montant à l'entier (calculs TVA, etc.) */
export function arrondirMontant(montant: number): number {
  return Math.round(montant);
}

/** Calcule la TVA et arrondit. Ex: HT=100000, taux=18 → TVA=18000 */
export function calculerTVA(montantHT: number, tauxTVA: number): number {
  return Math.round((montantHT * tauxTVA) / 100);
}

/** Calcule le TTC à partir du HT */
export function calculerTTC(montantHT: number, tauxTVA: number): number {
  return montantHT + calculerTVA(montantHT, tauxTVA);
}

/** Calcule le HT à partir du TTC */
export function calculerHTdepuisTTC(montantTTC: number, tauxTVA: number): number {
  return Math.round(montantTTC / (1 + tauxTVA / 100));
}

/**
 * Décomposition TTC → HT + TVA (entiers). TVA = TTC − HT après HT déduit du taux.
 * Ex. TTC 177000, taux 18 % → HT 150000, TVA 27000.
 */
export function decomposerTTC(montantTTC: number, tauxTVA: number): { montant_ht: number; montant_tva: number; montant_ttc: number } {
  const montant_ttc = Math.round(Number(montantTTC) || 0);
  const montant_ht = calculerHTdepuisTTC(montant_ttc, tauxTVA);
  const montant_tva = montant_ttc - montant_ht;
  return { montant_ht, montant_tva, montant_ttc };
}
